import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React from "react";
import "../../../../css/Customer/Pages/OurServicesPage/Service.css";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as MortagePlanningIcon } from "../../../../assets/icons/Mortgage-Planning.svg";
import { ReactComponent as CorporatePlanningIcon } from "../../../../assets/icons/Corporate-Planning.svg";
import { ReactComponent as InvestmentPlanningIcon } from "../../../../assets/icons/Investment-Planning.svg";
import { ReactComponent as EquityReleaseIcon } from "../../../../assets/icons/Equity -Release.svg";
import { ReactComponent as InsuranceProtectionIcon } from "../../../../assets/icons/Insurance-Protection.svg";
import { ReactComponent as RetirementPlanningIcon } from "../../../../assets/icons/Retirement-Planning.svg";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import { ReactComponent as NumberOneIcon } from "../../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../../assets/icons/Number 4.svg";
import Testimonials from "../../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
// import CustomerRetirementImage from "../../../../../assets/images/customer-retirement-min.jpeg";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
/**
 * MortgagePlanning component renders the Mortgage Planning service page.
 *
 * This page provides information and guidance on mortgage planning, including:
 * - Navigation bar with links to other pages.
 * - Hero section with a title and subtitle.
 * - Informational subtitles about mortgage services and advice.
 * - Frequently Asked Questions (FAQs) related to mortgages.
 * - List of related financial services offered.
 * - Adviser values section.
 * - Testimonials, contact form, and footer.
 *
 * @component
 * @returns {JSX.Element} The rendered Mortgage Planning service page.
 */
const MortagePlanning = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];

  const faqs = [
    {
      question: "Is there a difference between interest rates and APR?",
      answer:
        "Yes there is a difference with interest rates referring to the percent charged for borrowing the loan compared to APR which includes not only the interest rate but also other costs and fees associated with the loan.",
    },
    {
      question: "Can I qualify for a mortgage if I have filed for bankruptcy?",
      answer:
        "Yes you can qualify for a mortgage after filing for bankruptcy. However this is also dependant on how much time has passed since filing for bankruptcy.",
    },
    {
      question: "What fees are involved in getting a mortgage?",
      answer:
        "There are several types of fees that will be discussed with your adviser including loan origination fee, appraisal fee and credit report fee with a full plan to be talked through with your adviser.",
    },
    {
      question: "How does the process work?",
      answer:
        "Our advisers will speak with you regarding the best possible way to set up your mortgage and what plan would work best for you.",
    },
    {
      question: "Can I have two residential mortgages?",
      answer:
        "Yes you can but there are more conversations to be had with lenders when buying a possible second home, if you can meet the parameters put in place then you are likely to be able to buy a second home.",
    },
    {
      question: "How much can I borrow?",
      answer:
        "This depends on factors including, (but not limited to), income, credit score, property type & value. Our advisers can let you know the approximate amount you should be able to borrow.   ",
    },
    {
      question: "What length of time do I take a mortgage out for?",
      answer:
        "Mortgages typically come in 15-year fixed-rate and 30-year fixed-rate with the possibility to of 20 or 25 years, but these can be researched by your adviser to let you know which plan is best for you.",
    },
    {
      question: "Do I need insurance with my mortgage?",
      answer:
        "Yes, you usually do need insurance. There is building and contents insurance which protects your actual property and belongings from events like a fire or flood.  Also, life insurance to provide money for you or your family, should you die or become too ill to earn an income.    ",
    },
    {
      question: "How much deposit do I need?",
      answer:
        "This can vary for conventional loans there is a typical 20% deposit but this number can be flexible with other loans go as low as 3.5%.",
    },
  ];
  const values = [
    {
      icon: <NumberOneIcon />,
      text: "You are our most important person",
    },
    {
      icon: <NumberTwoIcon />,
      text: "Recommendations are planned out and delivered to you",
    },
    {
      icon: <NumberThreeIcon />,
      text: "Deliver the best outcomes for you",
    },
    {
      icon: <NumberFourIcon />,
      text: "We talk you through every step of the process",
    },
  ];

  return (
    <div className="Service">
      <SeoHelmet
        page={SEO_PAGES.mortgagePlanning}
        service={{
          name: "Mortgage Planning Advice",
          description:
            "Rosemount Financial Solutions provides mortgage planning advice for first-time buyers, home movers, remortgage clients and those with complex mortgage needs, including rental properties, home-buying schemes and credit issues.",
          serviceType: [
            "Mortgage Planning",
            "Mortgage Advice",
            "First-Time Buyer Mortgage Advice",
            "Remortgage Advice",
            "Home Mover Mortgage Advice",
            "Complex Mortgage Advice",
            "Buy to Let Mortgage Advice",
            "Mortgage Protection Advice",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Our Services",
            url: getPageUrl("/OurServices"),
          },
          {
            name: "Mortgage Planning",
            url: getPageUrl("/OurServices/MortgagePlanning"),
          },
        ]}
        faqs={faqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["customer-mortgage-min"]}
        altText="A couple holding a set of house keys with a home keyring."
        children={
          <>
            <h1>Mortgage Planning</h1>
            <div className="SubTitle">Best deal for your mortgage</div>
          </>
        }
      />
      <div className="SubTitle-Container">
        <div className="SubTitle">
          Our expert mortgage advisers provide tailored advice to help you
          navigate the complexities of securing the right mortgage for you.
          Whether you are a first-time buyer, looking to remortgage or buy your
          next home we offer a wide range of lenders and competitive deals.
        </div>
        <div className="SubTitle">
          Our advisers can also help with complex situations, involving rental
          properties, projects, home-buying schemes and credit issues. It's
          never too early to pick up the phone and speak with us about your
          mortgage options.
        </div>
        <div className="SubTitle">
          We can help guide you through the entire process offering clear,
          transparent advice ensure your mortgage aligns with your financial
          goals.
        </div>

        <div className="SubTitle">
          Your home may be repossessed if you do not keep up repayments on your
          mortgage.
        </div>
      </div>
      <FAQContainer faqs={faqs} />
      <div className="Customer-Services-Container">
        <h2>Our Services</h2>
        <div className="Customer-Services">
          <CardProduct
            Icon={CorporatePlanningIcon}
            title="Corporate Planning"
            link="/OurServices/CorporatePlanning"
          />
          <CardProduct
            Icon={InvestmentPlanningIcon}
            title="Investment Planning"
            link="/OurServices/InvestmentPlanning"
          />
          <CardProduct
            Icon={RetirementPlanningIcon}
            title="Retirement Planning"
            link="/OurServices/RetirementPlanning"
          />
          <CardProduct
            Icon={EquityReleaseIcon}
            title="Equity Release"
            link="/OurServices/EquityRelease"
          />
          <CardProduct
            Icon={InsuranceProtectionIcon}
            title="Insurance/ Protection"
            link="/OurServices/InsuranceProtection"
          />
        </div>
      </div>
      <AdviserValues title="Our Adviser Values" values={values} />
      <Testimonials />
      <ContactUs />
      <Footer />
    </div>
  );
};

export default MortagePlanning;
